// Mirrors Sentry's SeverityLevel without depending on the Sentry package here.
export type ErrorLevel =
  | 'fatal'
  | 'error'
  | 'warning'
  | 'log'
  | 'info'
  | 'debug'

export class BaseError extends Error {
  cause?: unknown
  meta?: Record<string, unknown>
  // Sentry severity for this error. Defaults to error-level when unset; set to
  // 'warning' for expected, user-correctable cases so they stay out of the
  // error feed while remaining recorded.
  level?: ErrorLevel

  constructor(
    message: string,
    meta?: Record<string, unknown>,
    cause?: unknown
  ) {
    super(message)
    this.name = 'BaseError'
    this.meta = meta
    this.cause = cause
  }

  toJSON() {
    return {
      name: this.name,
      message: this.message,
      ...(this.meta && { meta: JSON.stringify(this.meta) }),
      ...(this.cause instanceof Error && {
        cause: JSON.stringify(this.cause),
      }),
    }
  }
}

export class EnvironmentError extends BaseError {
  constructor(
    message: string,
    meta?: Record<string, unknown>,
    originalError?: unknown
  ) {
    const newMeta = {
      ...meta,
      env: process.env,
    }
    super(message, newMeta, originalError)
    this.name = 'EnvironmentError'
  }
}

export class AuthenticationError extends BaseError {
  constructor(message: string, originalError?: unknown) {
    super(message, {}, originalError)
    this.name = 'AuthenticationError'
  }
}

export class LtiError extends BaseError {
  constructor(message: string, meta?: unknown, originalError?: unknown) {
    const resolvedMeta =
      meta && typeof meta === 'object' && !(meta instanceof Error)
        ? (meta as Record<string, unknown>)
        : {}
    const resolvedCause =
      originalError ?? (meta instanceof Error ? meta : undefined)
    super(message, resolvedMeta, resolvedCause)
    this.name = 'LtiError'
  }
}

export class NotFoundError extends BaseError {
  constructor(
    message: string,
    meta?: Record<string, unknown>,
    originalError?: unknown
  ) {
    super(message, meta, originalError)
    this.name = 'NotFoundError'
  }
}
export class BadRequestError extends BaseError {
  constructor(
    message: string,
    meta?: Record<string, unknown>,
    originalError?: unknown
  ) {
    super(message, meta, originalError)
    this.name = 'BadRequestError'
  }
}
export class WebhookError extends BaseError {
  constructor(
    message: string,
    meta?: Record<string, unknown>,
    originalError?: unknown
  ) {
    super(message, meta, originalError)
    this.name = 'WebhookError'
  }
}
export class GoogleClientError extends BaseError {
  constructor(
    message: string,
    meta?: Record<string, unknown>,
    originalError?: unknown
  ) {
    super(message, meta, originalError)
    this.name = 'GoogleClientError'
  }
}

// Shared so the client can recognise this case from a server-action error
// response (which carries only the error `name`) without importing server code.
export const NO_GOOGLE_ACCOUNT_ERROR_NAME = 'NoGoogleAccountError'

// Expected, user-correctable: the teacher has no Google account we can deliver
// an editable copy to. Distinct name keeps it separable from unexpected
// failures in Sentry and lets the UI prompt them to connect one.
export class NoGoogleAccountError extends BaseError {
  constructor(
    message: string,
    meta?: Record<string, unknown>,
    originalError?: unknown
  ) {
    super(message, meta, originalError)
    this.name = NO_GOOGLE_ACCOUNT_ERROR_NAME
    // Expected and user-correctable (teacher just needs to connect Google) —
    // record it, but not in the error feed alongside genuine failures.
    this.level = 'warning'
  }
}

// Shared so the client can recognise this refusal from the error `name` alone,
// and name the rule instead of telling the teacher to retry.
export const LAST_VISIBLE_CLASS_ERROR_NAME = 'LastVisibleClassError'

// Expected, user-correctable: hiding or deleting this class would leave the
// project with no visible class.
export class LastVisibleClassError extends BaseError {
  constructor(
    message: string,
    meta?: Record<string, unknown>,
    originalError?: unknown
  ) {
    super(message, meta, originalError)
    this.name = LAST_VISIBLE_CLASS_ERROR_NAME
    this.level = 'warning'
  }
}

export class FileError extends BaseError {
  constructor(
    message: string,
    meta?: Record<string, unknown>,
    originalError?: unknown
  ) {
    super(message, meta, originalError)
    this.name = 'FileError'
  }
}
export class CloudinaryError extends BaseError {
  constructor(
    message: string,
    meta?: Record<string, unknown>,
    originalError?: unknown
  ) {
    super(message, meta, originalError)
    this.name = 'CloudinaryError'
  }
}

export class ModelError extends BaseError {
  constructor(
    message: string,
    meta?: Record<string, unknown>,
    originalError?: unknown
  ) {
    super(message, meta, originalError)
    this.name = 'ModelError'
  }
}

export class PublishingValidationError extends BaseError {
  constructor(
    message: string,
    meta?: Record<string, unknown>,
    originalError?: unknown
  ) {
    super(message, meta, originalError)
    this.name = 'PublishingValidationError'
  }
}

export class EmailError extends BaseError {
  constructor(
    message: string,
    meta?: Record<string, unknown>,
    originalError?: unknown
  ) {
    super(message, meta, originalError)
    this.name = 'EmailError'
  }
}

export class ValidationError extends BaseError {
  constructor(errors: Record<string, string>, originalError?: unknown) {
    super('Error while processing input payload', { errors }, originalError)
    this.name = 'ValidationError'
  }
}
